/**
 * Reads reviews out of Air Reviews for the two elements below: the written list, and the
 * strip of customer photos under it.
 *
 * The app never hands the storefront the whole review list. On a product template it
 * exposes one page; on every other template -- this shop's homepage included -- it sets
 * `productReview` to false and exposes none at all. Reading whatever the app had painted
 * into the page therefore always looked like "that is all of them", so the list topped out
 * at a single page and "See more" vanished after one click.
 *
 * The app's own widget gets its pages from a public GET on app.airreviews.io, keyed by the
 * shop id it publishes in window.AIR_REVIEWS. We call the same endpoint and walk it with
 * its `after` cursor until it runs out of pages, which is what actually yields every
 * approved review for the product.
 */
const REVIEWS_ENDPOINT = 'https://app.airreviews.io/review/loadReviews';
const REVIEWS_PER_REQUEST = 100;
/** A stop, so a response that keeps looking like there is more cannot spin forever. */
const MAX_REQUESTS = 50;
/** window.AIR_REVIEWS is injected by the app embed, which may land after we connect. */
const SHOP_ID_TIMEOUT = 15000;

/** @type {Map<string, {pages: Array<Array<object>>, listeners: Set<Function>, walk: Promise<void>}>} */
const reviewWalks = new Map();

/** How far ahead of the viewport to start loading, so the list is filled before it is seen. */
const PRELOAD_MARGIN = '800px';

/**
 * Runs `start` when the element is near the viewport, rather than at page load.
 *
 * The reviews sit near the foot of the page and cost real work to fill -- a request per
 * hundred reviews and a card each -- which most visitors never scroll far enough to see.
 * Waiting until the section is approaching keeps that off the initial load, which is where
 * it hurts on a phone.
 *
 * @param {HTMLElement} element
 * @param {() => void} start
 * @returns {IntersectionObserver|null} the observer to disconnect on teardown, if one is used
 */
function whenApproaching(element, start) {
  // The theme editor renders sections out of view and merchants expect to see content, and
  // a browser without IntersectionObserver should not simply get nothing.
  if (typeof IntersectionObserver !== 'function' || window.Shopify?.designMode) {
    start();
    return null;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      observer.disconnect();
      start();
    },
    { rootMargin: PRELOAD_MARGIN }
  );
  observer.observe(element);
  return observer;
}

/**
 * The shop id lives in the app embed's inline payload, which may not have run yet.
 *
 * @param {() => boolean} isAborted lets a element that has been torn down stop the poll
 * @returns {Promise<string|null>}
 */
function resolveShopId(isAborted) {
  const read = () => window.AIR_REVIEWS?.settings?.shopId || null;
  const immediate = read();
  if (immediate) return Promise.resolve(immediate);

  return new Promise((resolve) => {
    const deadline = Date.now() + SHOP_ID_TIMEOUT;
    const timer = window.setInterval(() => {
      const shopId = read();
      if (shopId || isAborted() || Date.now() > deadline) {
        window.clearInterval(timer);
        resolve(shopId);
      }
    }, 250);
  });
}

/**
 * Requests every page of reviews for a product, handing each one on as it lands rather
 * than at the end, so both elements fill in while the rest is still in flight.
 *
 * @param {string} shopId
 * @param {string|undefined} productId
 * @param {(reviews: Array<object>) => void} onPage
 */
async function walkReviewPages(shopId, productId, onPage) {
  let after = null;

  for (let request = 0; request < MAX_REQUESTS; request += 1) {
    const query = new URLSearchParams({
      shopId,
      minimumRating: 'all-reviews',
      limitPerPage: String(REVIEWS_PER_REQUEST),
    });
    if (productId) query.set('productId', productId);
    if (after) query.set('after', after);

    const response = await fetch(`${REVIEWS_ENDPOINT}?${query}`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) break;

    const reviews = (await response.json())?.data?.reviews;
    if (!Array.isArray(reviews) || reviews.length === 0) break;
    onPage(reviews);

    // A short page is the last page. The response also carries `hasNext`, but a full page
    // is the only signal that survives the app changing its own page size.
    if (reviews.length < REVIEWS_PER_REQUEST) break;

    // The cursor is the last id of the page. If it has not moved, stop: another request
    // would hand back the same page.
    const cursor = reviews.at(-1)?.id;
    if (!cursor || cursor === after) break;
    after = cursor;
  }
}

/**
 * Walks the pages once per shop and product, however many elements ask. Pages already in
 * hand are replayed to a late subscriber, so it does not matter which element connects
 * first or whether the requests have finished by then.
 *
 * @param {string} shopId
 * @param {string|undefined} productId
 * @param {(reviews: Array<object>) => void} onPage
 * @returns {Promise<void>} resolves when every page has been handed over
 */
function loadAllReviews(shopId, productId, onPage) {
  const key = `${shopId}|${productId ?? ''}`;
  let entry = reviewWalks.get(key);

  if (!entry) {
    entry = { pages: [], listeners: new Set(), walk: null };
    reviewWalks.set(key, entry);
    entry.walk = walkReviewPages(shopId, productId, (reviews) => {
      entry.pages.push(reviews);
      for (const listener of entry.listeners) {
        // One element throwing must not stop the pages reaching the other one.
        try {
          listener(reviews);
        } catch (error) {
          /* the listener's own problem */
        }
      }
    });
  }

  for (const page of entry.pages) onPage(page);
  entry.listeners.add(onPage);
  return entry.walk.finally(() => entry.listeners.delete(onPage));
}

class MelaxinWrittenReviews extends HTMLElement {
  connectedCallback() {
    this.moreButton = this.querySelector('[data-written-review-more]');
    this.reveal = this.querySelector('[data-written-review-reveal]');
    this.pagination = this.querySelector('[data-written-review-pagination]');
    this.list = this.querySelector('[data-written-review-list]');
    this.collapsedCount = Number(this.dataset.collapsedCount) || 2;
    this.pageSize = Number(this.dataset.pageSize) || 10;
    this.showAllLoaded = this.dataset.showAllLoaded === 'true';
    this.seenReviewIds = new Set();
    this.loading = false;
    this.hasMorePages = false;
    this.aborted = false;
    this.ratingFilter = 'all';
    this.sortFilter = 'oldest';

    this.moreButton?.addEventListener('click', this.expand);
    document.addEventListener('melaxin:review-filter', this.handleReviewFilter);
    document.addEventListener('shopify:block:select', this.handleBlockSelect);
    this.cards = [...this.querySelectorAll('[data-written-review-card]')];
    this.visibleCount = this.showAllLoaded ? this.cards.length : Math.min(this.collapsedCount, this.cards.length);
    this.moreLabel = this.moreButton?.textContent ?? '';
    this.renderVisible();
    this.approachObserver = whenApproaching(this, () => this.load());
  }

  disconnectedCallback() {
    this.aborted = true;
    this.approachObserver?.disconnect();
    this.moreButton?.removeEventListener('click', this.expand);
    document.removeEventListener('melaxin:review-filter', this.handleReviewFilter);
    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    this.stopWatching();
  }

  async load() {
    const shopId = await resolveShopId(() => this.aborted);
    if (this.aborted) return;
    if (shopId && (await this.loadFromApi(shopId))) return;
    // No shop id, no reviews for this product, or the endpoint refused: fall back to
    // reading whatever the app managed to render, which beats showing nothing.
    this.watchForAppReviews();
  }

  /**
   * @param {string} shopId
   * @returns {Promise<boolean>} whether any review was added
   */
  async loadFromApi(shopId) {
    let added = 0;
    this.loading = true;
    this.hasMorePages = true;

    try {
      await loadAllReviews(shopId, this.dataset.productId, (reviews) => {
        if (this.aborted) return;
        added += this.appendReviews(reviews);
      });
    } catch (error) {
      return false;
    } finally {
      this.loading = false;
      this.hasMorePages = false;
      this.renderVisible();
    }

    return added > 0;
  }

  /**
   * The app fills its review list in stages, so the first batch to arrive is usually not
   * all of them; re-running picks up each later batch, and appending already skips
   * anything it has added before.
   */
  watchForAppReviews() {
    this.hydrateAppReviews();

    const deadline = Date.now() + 30000;
    this.appReviewsPoll = window.setInterval(() => {
      this.hydrateAppReviews();
      if (Date.now() > deadline) this.stopWatching();
    }, 500);
  }

  stopWatching() {
    if (this.appReviewsPoll) window.clearInterval(this.appReviewsPoll);
    this.appReviewsPoll = null;
  }

  /**
   * "See more" loads another page of reviews underneath the ones already shown and stays
   * put at the bottom of the list, rather than switching the list over to paged dots. With
   * a hundred or more reviews, paging hid most of them behind a control people had to
   * discover; loading in place keeps one obvious way forward.
   */
  expand = () => {
    this.classList.remove('is-collapsed');
    this.classList.add('is-expanded');
    this.visibleCount = Math.min(this.visibleCount + this.pageSize, this.cards.length);
    this.renderVisible();
  };

  handleBlockSelect = (event) => {
    const card = event.target?.closest?.('[data-written-review-card]');
    if (!card || !this.contains(card)) return;
    const index = this.cards.indexOf(card);
    if (index < 0) return;
    this.classList.remove('is-collapsed');
    this.classList.add('is-expanded');
    this.visibleCount = Math.max(this.visibleCount, index + 1);
    this.renderVisible();
  };

  handleReviewFilter = (event) => {
    const { sectionId, kind, value } = event.detail || {};
    if (sectionId !== this.dataset.sectionId) return;

    if (kind === 'rating') this.ratingFilter = value || 'all';
    if (kind === 'sort') this.sortFilter = value || 'newest';
    if (kind === 'rating' || kind === 'sort') this.applySort();
    this.visibleCount = this.showAllLoaded ? this.cards.length : this.collapsedCount;
    this.classList.toggle('is-filtered', this.ratingFilter !== 'all' || this.sortFilter === 'image');
    this.renderVisible();
  };

  renderVisible() {
    const visibleCards = this.cards.filter((card) => this.matchesFilters(card));
    visibleCards.forEach((card, index) => {
      card.hidden = index >= this.visibleCount;
    });
    this.cards.filter((card) => !visibleCards.includes(card)).forEach((card) => {
      card.hidden = true;
    });

    const remaining = Math.max(0, visibleCards.length - this.visibleCount);
    // Keep the button up while later pages are still on their way, so someone who clicks
    // through everything loaded so far does not watch it disappear mid-fetch.
    const fetching = this.loading && this.hasMorePages;
    if (this.reveal instanceof HTMLElement) this.reveal.hidden = remaining <= 0 && !fetching;
    if (this.moreButton instanceof HTMLElement) {
      this.moreButton.disabled = remaining <= 0 && fetching;
      this.moreButton.setAttribute('aria-busy', String(this.moreButton.disabled));
      if (remaining > 0) {
        const step = Math.min(this.pageSize, remaining);
        const template = this.moreButton.dataset.labelTemplate || this.moreLabel;
        this.moreButton.textContent = template.replace('[count]', String(step));
      }
    }
    if (this.pagination instanceof HTMLElement) this.pagination.hidden = true;
  }

  matchesFilters(card) {
    const hasImage =
      card.dataset.reviewHasImage === 'true' ||
      Boolean(card.querySelector('.melaxin-written-review__images'));
    if (this.sortFilter === 'image' && !hasImage) return false;
    return true;
  }

  applySort() {
    if (!(this.list instanceof HTMLElement)) return;

    const selectedRating = Number(this.ratingFilter);
    const hasSelectedRating = Number.isInteger(selectedRating) && selectedRating >= 1 && selectedRating <= 5;

    const order = [...this.cards].sort((a, b) => {
      if (hasSelectedRating) {
        const aRating = Number(a.dataset.reviewRating) || 0;
        const bRating = Number(b.dataset.reviewRating) || 0;
        const aDistance = aRating >= 1 && aRating <= 5 ? (aRating - selectedRating + 5) % 5 : 5;
        const bDistance = bRating >= 1 && bRating <= 5 ? (bRating - selectedRating + 5) % 5 : 5;
        if (aDistance !== bDistance) return aDistance - bDistance;
      }

      const aDate = Date.parse(a.dataset.reviewCreatedAt || '') || 0;
      const bDate = Date.parse(b.dataset.reviewCreatedAt || '') || 0;
      if (this.sortFilter === 'oldest') return aDate - bDate || this.cardOrder(a) - this.cardOrder(b);
      return bDate - aDate || this.cardOrder(a) - this.cardOrder(b);
    });
    this.cards = order;
    for (const card of order) this.list.append(card);
  }

  cardOrder(card) {
    return Number(card.dataset.reviewOrder) || 0;
  }

  /** @returns {boolean} whether app review data was found */
  hydrateAppReviews() {
    const air = window.AIR_REVIEWS;
    const appReviews =
      [air?.settings?.allReviewsPage, air?.settings?.reviews, air?.reviews, air?.data?.reviews].find(
        (value) => Array.isArray(value) && value.length > 0
      ) ?? this.readRenderedReviews();

    if (!appReviews || appReviews.length === 0) return false;
    this.appendReviews(appReviews);
    return true;
  }

  /**
   * @param {Array<object>} reviews
   * @returns {number} how many were added
   */
  appendReviews(reviews) {
    if (!(this.list instanceof HTMLElement)) return 0;

    // Reviews typed into the section are matched by their text, so one that also comes back
    // from the app is not shown twice when the section is set to keep both.
    const placeholderCopy = new Set(
      [...this.querySelectorAll('[data-placeholder] .melaxin-written-review__copy')].map((copy) =>
        this.normalize(copy.textContent)
      )
    );
    const productId = Number(this.dataset.productId);
    // Reviews that actually said something -- words, photos or both -- are the ones worth
    // reading first, so they collect above the ones that left only a rating. Two fragments
    // rather than one sort at the end, because reviews arrive a page at a time and the
    // split has to survive each later batch.
    const told = document.createDocumentFragment();
    const ratedOnly = document.createDocumentFragment();
    let added = 0;

    for (const review of reviews) {
      const id = String(review.id ?? '');
      if (id && this.seenReviewIds.has(id)) continue;
      if (review.status && review.status !== 'approved') continue;

      // Only filter by product when the review says which product it belongs to. Reviews
      // read back out of the page carry no ids, and the app has already scoped that list
      // to this product, so an id check there would discard every one of them.
      const reviewProductIds = [...(review.productIds ?? []), review.productId]
        .map(Number)
        .filter(Number.isFinite);
      if (productId && reviewProductIds.length > 0 && !reviewProductIds.includes(productId)) continue;

      // A rating left without words is still one of the reviews the count promises, so it
      // gets a card too -- just without a body. Deduping on text would collapse every one
      // of those into a single card, which is why ids do that job here.
      const content = String(review.content ?? '').trim();
      if (content && placeholderCopy.has(this.normalize(content))) continue;

      if (id) this.seenReviewIds.add(id);
      const card = this.createReviewCard(review);
      (card.hasAttribute('data-review-rating-only') ? ratedOnly : told).append(card);
      added += 1;
    }

    if (added === 0) return 0;
    if (told.childElementCount > 0) {
      // Slot this page's readable reviews in above the rating-only run that is already there.
      const firstRatingOnly = this.list.querySelector('[data-review-rating-only]');
      if (firstRatingOnly) this.list.insertBefore(told, firstRatingOnly);
      else this.list.append(told);
    }
    if (ratedOnly.childElementCount > 0) this.list.append(ratedOnly);

    // Drop the placeholder reviews typed into the section once real ones are available,
    // unless the section is set to keep them alongside.
    if (this.dataset.replacePlaceholders !== 'false') {
      for (const card of this.querySelectorAll('[data-written-review-card][data-placeholder]')) card.remove();
    }

    this.cards = [...this.querySelectorAll('[data-written-review-card]')];
    this.applySort();
    // Real reviews replaced the placeholders, so the revealed count can exceed what is
    // left. Never show fewer than the collapsed count.
    this.visibleCount = this.showAllLoaded
      ? this.cards.length
      : Math.min(Math.max(this.visibleCount, this.collapsedCount), this.cards.length);
    this.renderVisible();
    return added;
  }

  /**
   * Reads the reviews the app has already painted into the page.
   *
   * Class names are matched loosely because the app hashes and renames them between
   * versions; anything that cannot be parsed into a body of text is skipped rather than
   * rendered as an empty card.
   *
   * @returns {Array<object>}
   */
  readRenderedReviews() {
    const items = document.querySelectorAll(
      '.AirReviews-Blocks__ListReview, [class*="AirReviews"][class*="ListReview"], [class*="AirReviews"][class*="ReviewItem"]'
    );
    if (items.length === 0) return [];

    const pick = (root, patterns) => {
      for (const pattern of patterns) {
        const found = root.querySelector(pattern);
        const text = found?.textContent?.trim();
        if (text) return text;
      }
      return '';
    };

    const reviews = [];
    for (const item of items) {
      if (!(item instanceof HTMLElement)) continue;

      const content = pick(item, [
        '[class*="Content"]',
        '[class*="Body"]',
        '[class*="Description"]',
        '[class*="Message"]',
        'p',
      ]);
      if (!content) continue;

      const name =
        pick(item, ['[class*="Author"]', '[class*="Reviewer"]', '[class*="CustomerName"]', '[class*="Name"]']) ||
        'Customer';

      // Rating comes from an aria-label such as "5 out of 5" where the app provides one,
      // and otherwise from however many star elements are marked as filled.
      const label = item.querySelector('[aria-label*="out of"], [title*="out of"]');
      const fromLabel = Number(
        (label?.getAttribute('aria-label') ?? label?.getAttribute('title') ?? '')
          .match(/([0-5](?:[.,]\d)?)/)?.[1]
          ?.replace(',', '.')
      );
      const filled = item.querySelectorAll(
        '[class*="Star"][class*="ill"], [class*="Star"][class*="ctive"], [class*="Star"][class*="elected"]'
      ).length;
      const rate = Number.isFinite(fromLabel) && fromLabel > 0 ? fromLabel : filled || 5;

      reviews.push({
        // Scraped reviews carry no id of their own, so stand one up from the text. Polling
        // re-reads the same nodes, and this is what stops them being added twice.
        id: `rendered:${this.normalize(content)}`,
        content,
        firstName: name,
        rate,
        date: pick(item, ['time', '[class*="Date"]', '[class*="Time"]']),
      });
    }

    return reviews;
  }

  createReviewCard(review) {
    const name = this.formatReviewerName(review.firstName || review.first_name || review.lastName || 'Customer');
    const rating = Math.min(5, Math.max(1, Number(review.rate) || 5));
    const reviewDate = this.getReviewDate(review);
    const card = document.createElement('article');
    card.className = 'melaxin-written-review';
    card.setAttribute('data-written-review-card', '');
    card.dataset.reviewRating = String(rating);
    card.dataset.reviewCreatedAt = reviewDate;
    card.dataset.reviewOrder = String(this.cards.length);

    const identity = document.createElement('div');
    identity.className = 'melaxin-written-review__identity';
    const avatar = document.createElement('span');
    avatar.className = 'melaxin-written-review__avatar';
    avatar.setAttribute('aria-hidden', 'true');
    avatar.textContent = this.getInitials(review.firstName || review.first_name || review.lastName || 'Customer');

    const person = document.createElement('div');
    person.className = 'melaxin-written-review__person';
    const reviewer = document.createElement('strong');
    reviewer.textContent = name;
    person.append(reviewer);
    const verified = document.createElement('span');
    verified.className = 'melaxin-written-review__verified';
    verified.textContent = `\u2713 ${this.dataset.verifiedLabel || 'Verifizierter Käufer'}`;
    person.append(verified);
    identity.append(avatar, person);

    const content = document.createElement('div');
    content.className = 'melaxin-written-review__content';
    const ratingLine = document.createElement('div');
    ratingLine.className = 'melaxin-written-review__rating-line';
    const stars = document.createElement('div');
    stars.className = 'melaxin-written-review__stars';
    stars.setAttribute('aria-label', `${rating} out of 5 stars`);
    stars.textContent = `${'★'.repeat(rating)}${'☆'.repeat(5 - rating)}`;
    ratingLine.append(stars);
    const relativeDate = this.formatRelativeDate(reviewDate);
    if (relativeDate) {
      const date = document.createElement('time');
      date.className = 'melaxin-written-review__date';
      date.dateTime = reviewDate;
      date.textContent = relativeDate;
      ratingLine.append(date);
    }
    content.append(ratingLine);

    const product = document.createElement('h3');
    product.className = 'melaxin-written-review__product';
    product.textContent = this.dataset.productTitle || '';
    content.append(product);

    const copyText = String(review.content ?? '').trim();
    if (copyText) {
      const copy = document.createElement('div');
      copy.className = 'melaxin-written-review__copy';
      copy.textContent = copyText;
      content.append(copy);
    }

    const photos = this.createReviewPhotos(review);
    if (photos) content.append(photos);
    card.dataset.reviewHasImage = String(this.hasReviewImages(review) || Boolean(photos));

    // A rating with nothing else to it sinks to the bottom of the list. Marked on the card
    // so appendReviews can place it without re-reading the review.
    if (!copyText && !photos) card.setAttribute('data-review-rating-only', '');

    card.append(identity, content);
    return card;
  }

  /**
   * The photos a reviewer attached, shown on their own card rather than only in the strip
   * above, so a review and its evidence stay together.
   *
   * @returns {HTMLElement|null} null when the review came without photos
   */
  createReviewPhotos(review) {
    const sources = this.reviewImageSources(review);
    if (sources.length === 0) return null;

    const row = document.createElement('div');
    row.className = 'melaxin-written-review__images';

    for (const source of sources) {
      const item = document.createElement('div');
      item.className = 'melaxin-written-review__image';

      const image = document.createElement('img');
      image.src = source;
      image.setAttribute('loading', 'lazy');
      image.setAttribute('decoding', 'async');
      image.alt = '';
      // Customer uploads on someone else's storage. One that has gone missing should take
      // its own tile with it, and the row too if it was the only one.
      image.addEventListener(
        'error',
        () => {
          item.remove();
          if (row.childElementCount === 0) row.remove();
        },
        { once: true }
      );

      item.append(image);
      row.append(item);
    }

    return row;
  }

  reviewImageSources(review) {
    const raw = review.images ?? review.imageUrls ?? review.photos ?? review.reviewImages ?? review.media ?? [];
    const images = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return images
      .map((image) => {
        if (typeof image === 'string') return image.trim();
        if (!image || typeof image !== 'object') return '';
        return String(image.url ?? image.src ?? image.image ?? image.originalUrl ?? '').trim();
      })
      .filter(Boolean);
  }

  hasReviewImages(review) {
    return review.hasImages === true || review.has_images === true || this.reviewImageSources(review).length > 0;
  }

  getReviewDate(review) {
    return String(
      review.createdAt ||
        review.created_at ||
        review.updatedAt ||
        review.updated_at ||
        review.date ||
        review.time ||
        ''
    ).trim();
  }

  formatRelativeDate(value) {
    if (!value) return '';
    if (/^\d+\s+(second|minute|hour|day|week|month|year)s?\s+ago$/i.test(value)) return value;

    const timestamp = Date.parse(value);
    if (!Number.isFinite(timestamp)) return '';

    const elapsedSeconds = Math.max(0, Math.floor((Date.now() - timestamp) / 1000));
    const units = [
      ['year', 31536000],
      ['month', 2592000],
      ['week', 604800],
      ['day', 86400],
      ['hour', 3600],
      ['minute', 60],
    ];
    const unit = units.find(([, seconds]) => elapsedSeconds >= seconds);
    if (!unit) return 'Just now';

    const [name, seconds] = unit;
    const count = Math.floor(elapsedSeconds / seconds);
    return `${count} ${name}${count === 1 ? '' : 's'} ago`;
  }

  formatReviewerName(value) {
    const parts = String(value).trim().split(/\s+/).filter(Boolean);
    if (parts.length < 2) return parts[0] || 'Customer';
    const last = parts.at(-1);
    const initial = /^[A-Za-z]\.$/.test(last) ? last : `${last.charAt(0)}.`;
    return `${parts[0]} ${initial}`;
  }

  getInitials(value) {
    const parts = String(value).trim().split(/\s+/).filter(Boolean);
    if (parts.length < 2) return (parts[0] || 'C').slice(0, 2).toUpperCase();
    return `${parts[0].charAt(0)}${parts.at(-1).charAt(0)}`.toUpperCase();
  }

  normalize(value) {
    return String(value ?? '').replace(/\s+/g, ' ').trim().toLocaleLowerCase();
  }
}

/**
 * The strip of customer photos that sits under the write-a-review button. Air Reviews
 * hangs image urls off the reviews themselves, so this is the same walk the list above
 * does -- shared, so the pages are only requested once -- filtered down to the ones that
 * came with a photo.
 *
 * It starts hidden and only reveals itself once a photo has actually loaded, because a
 * store with no review photos should show nothing rather than an empty rail.
 */
class MelaxinReviewImages extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-review-images-track]');
    this.seenImages = new Set();
    this.aborted = false;
    this.swipeable = this.dataset.swipeable === 'true';
    this.visibleCount = Math.max(1, Number.parseInt(this.dataset.visibleCount, 10) || 3);
    this.toggleAttribute('data-swipeable', this.swipeable);
    if (this.track instanceof HTMLElement) this.track.tabIndex = this.swipeable ? 0 : -1;
    // This element starts with the native `hidden` attribute, so observing it directly
    // can never fire: display:none elements do not intersect the viewport. Observe the
    // visible reviews section instead and reveal this strip only after photos arrive.
    const approachTarget = this.closest('.melaxin-content__reviews') || this.parentElement || this;
    this.approachObserver = whenApproaching(approachTarget, () => this.load());
  }

  disconnectedCallback() {
    this.aborted = true;
    this.approachObserver?.disconnect();
  }

  async load() {
    const shopId = await resolveShopId(() => this.aborted);
    if (!shopId || this.aborted) return;

    try {
      await loadAllReviews(shopId, this.dataset.productId, (reviews) => {
        if (this.aborted) return;
        this.appendImages(reviews);
      });
    } catch (error) {
      /* Nothing to show. The strip stays hidden. */
    }
  }

  appendImages(reviews) {
    if (!(this.track instanceof HTMLElement)) return;
    const fragment = document.createDocumentFragment();
    let reachedImageLimit = false;

    for (const review of reviews) {
      if (review.status && review.status !== 'approved') continue;
      for (const image of review.images ?? []) {
        if (!this.swipeable && this.track.childElementCount + fragment.childElementCount >= this.visibleCount) {
          reachedImageLimit = true;
          break;
        }
        const source = String(image ?? '').trim();
        if (!source || this.seenImages.has(source)) continue;
        this.seenImages.add(source);
        fragment.append(this.createImage(source, review));
      }
      if (reachedImageLimit) break;
    }

    if (fragment.childElementCount === 0) return;
    this.track.append(fragment);
    this.hidden = false;
  }

  createImage(source, review) {
    const item = document.createElement('div');
    item.className = 'melaxin-review-images__item';

    const image = document.createElement('img');
    image.src = source;
    image.setAttribute('loading', 'lazy');
    image.setAttribute('decoding', 'async');
    // No description comes back with the photo, so the reviewer's name is the only true
    // thing we can say about it.
    image.alt = this.reviewerName(review);
    // These are customer uploads on someone else's storage. One that has gone missing
    // should take its own tile with it rather than leave a broken frame in the rail.
    image.addEventListener(
      'error',
      () => {
        item.remove();
        if (this.track instanceof HTMLElement && this.track.childElementCount === 0) this.hidden = true;
      },
      { once: true }
    );

    item.append(image);
    return item;
  }

  reviewerName(review) {
    const parts = String(review.firstName ?? review.first_name ?? '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    return parts.join(' ');
  }
}

if (!customElements.get('melaxin-written-reviews')) {
  customElements.define('melaxin-written-reviews', MelaxinWrittenReviews);
}

if (!customElements.get('melaxin-review-images')) {
  customElements.define('melaxin-review-images', MelaxinReviewImages);
}
